/**
 * The Documents tab's column track list, shared by the header and every row.
 *
 * One definition rather than two: the header and `DocumentRow` are separate
 * components with the same grid, and a track added to one and not the other
 * misaligns the whole table.
 *
 * The track count follows the cells both components actually render, so the
 * procedure basis is a parameter: `Sample Match` renders only under a sample
 * basis. Keying on the analytics column alone leaves a direct-basis grid one
 * track wider than it has cells, which does not misalign the headings against
 * their controls — the two shift together — but does hand each column the
 * width meant for the one before it and leave a phantom trailing track.
 *
 * Every class name is written out in full because Tailwind reads the source
 * text — an interpolated track list produces no class at all.
 */
const WITHOUT_ANALYTICS_SAMPLE =
  "grid-cols-[40px_minmax(300px,2fr)_160px_160px_48px]";

const WITHOUT_ANALYTICS_DIRECT =
  "grid-cols-[40px_minmax(300px,2fr)_160px_48px]";

/**
 * Analytics contributes two tracks, not one. The parameters a document answers
 * and the analytics-input flag answer different questions, and the flag gates
 * the parameter editor — a single shared cell cannot show one gating the other.
 *
 * They sit ahead of `Document Category`, which stays the last column an auditor
 * reads: it is the same concept the Support tab shows as `Type`, and the two
 * tabs are read against each other.
 *
 * `For Analytics Test` takes the 160px track and `Workflow Parameters` the 200px
 * one after it — the parameter editor names a column and a parameter per line,
 * where the flag is one switch. The widths are positional, so they move with the
 * cells whenever the pair is reordered.
 */
const WITH_ANALYTICS_SAMPLE =
  "grid-cols-[40px_minmax(300px,2fr)_160px_160px_200px_160px_48px]";

const WITH_ANALYTICS_DIRECT =
  "grid-cols-[40px_minmax(300px,2fr)_160px_200px_160px_48px]";

export function documentsGridColumns(
  showsAnalyticsColumns: boolean,
  procedureBasis: "sample" | "direct",
): string {
  if (showsAnalyticsColumns) {
    return procedureBasis === "sample"
      ? WITH_ANALYTICS_SAMPLE
      : WITH_ANALYTICS_DIRECT;
  }
  return procedureBasis === "sample"
    ? WITHOUT_ANALYTICS_SAMPLE
    : WITHOUT_ANALYTICS_DIRECT;
}
